import { Metadata, RunView, UserInfo } from '@memberjunction/core';
import { UUIDsEqual } from '@memberjunction/global';
import { scopeUserToOrganization } from '@bluecypress/bcsaas-core';
import { BCAuthorizationEngine } from '@bluecypress/bcsaas-authorizations';
import { BCCredentialEngine } from '@bluecypress/bcsaas-credentials';

/**
 * Organization credential writes for the Izzy admin panels, through BCSaaS's credential engine.
 *
 * Creating and deleting go through `BCCredentialEngine` so the BCSaaS credential-use junction
 * (BC: Organization Credential Uses) is kept in step with the credential: an "AI Vendor API Key" is
 * linked to its vendor's Credential Use on create, and the junction rows are removed with the
 * organization link on delete. BCSaaS's AI configuration engine reads that junction to decide
 * whether an organization holds a vendor's key; without it every activation was refused.
 *
 * Values are still sent as plain JSON from the browser and encrypted server-side by Izzy's
 * MJ: Credentials subclass, exactly as before. Editing a credential is NOT routed here: the
 * engine's update rewrites the stored values from what the browser holds, and the browser never
 * holds the decrypted values, so a name-only edit would erase the key.
 */

/** Name of the credential type Izzy stores AI vendor keys under. */
export const AI_VENDOR_KEY_TYPE = 'AI Vendor API Key';

const AI_VENDORS_ENTITY = 'MJ: AI Vendors';

/** BCSaaS stores a credential's name as `Name [organizationID]`; this matches that suffix. */
const ORG_SCOPED_NAME_SUFFIX = / \[[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}\]$/;

/** The credential's name without BCSaaS's ` [organizationID]` suffix, for display. */
export function DisplayCredentialName(name: string | null | undefined): string {
  return (name ?? '').replace(ORG_SCOPED_NAME_SUFFIX, '');
}

/** The ` [organizationID]` suffix of a stored credential name, or '' when it has none. */
export function CredentialNameSuffix(name: string | null | undefined): string {
  return (name ?? '').match(ORG_SCOPED_NAME_SUFFIX)?.[0] ?? '';
}

export interface CreateOrgCredentialParams {
  organizationID: string;
  credentialTypeName: string;
  name: string;
  values: Record<string, string>;
  description?: string | null;
  expiresAt?: Date | null;
}

/**
 * Creates a credential for an organization: the MJ credential, the organization link and, for an AI
 * vendor key, the junction row to that vendor's Credential Use, in one engine call.
 * Throws with the engine's message on any failure (authorization, schema validation, save).
 */
export async function CreateOrgCredential(params: CreateOrgCredentialParams): Promise<void> {
  const { user, engine } = await loadEngines(params.organizationID);
  const credentialUseIDs = await credentialUseIDsFor(params.credentialTypeName, params.values, engine);
  await engine.StoreCredential(params.credentialTypeName, params.name, params.values, user, {
    description: params.description ?? undefined,
    expiresAt: params.expiresAt ?? undefined,
    credentialUseIDs: credentialUseIDs.length > 0 ? credentialUseIDs : undefined,
  });
}

/**
 * Deletes a credential from an organization: its junction rows and organization link, and the MJ
 * credential itself when no other organization link uses it. Izzy's server-side MJ: Credentials
 * subclass still clears Izzy's own references when the credential row is deleted.
 */
export async function DeleteOrgCredential(organizationID: string, credentialID: string): Promise<void> {
  const { user, engine } = await loadEngines(organizationID);
  const links = engine.OrganizationCredentials.filter(oc => UUIDsEqual(oc.CredentialID, credentialID));
  const link = links.find(oc => UUIDsEqual(oc.OrganizationID, organizationID));
  if (!link) {
    throw new Error('This credential is not linked to the selected organization.');
  }
  await engine.DeleteCredentialByID(link.ID, user, { deleteUnderlying: links.length === 1 });
}

/**
 * The engines act on the user's tenant organization, so the user is scoped to the organization
 * chosen in the panel (a copy; the session user is untouched). The authorization engine is loaded
 * here because the credential engine checks BCSaaS.Credentials.Write / .Delete and nothing else in
 * Izzy loads it.
 */
async function loadEngines(organizationID: string): Promise<{ user: UserInfo; engine: BCCredentialEngine }> {
  const current = new Metadata().CurrentUser;
  if (!current) {
    throw new Error('No signed-in user.');
  }
  const user = scopeUserToOrganization(current, organizationID);
  await BCAuthorizationEngine.Instance.Config(false, user);
  await BCCredentialEngine.Instance.Config(true, user);
  return { user, engine: BCCredentialEngine.Instance };
}

/**
 * For an AI vendor key, the active Credential Use of the vendor its driver class belongs to
 * (driver class → MJ: AI Model Vendors → vendor → BC: Credential Uses). Other types get none.
 */
async function credentialUseIDsFor(
  credentialTypeName: string,
  values: Record<string, string>,
  engine: BCCredentialEngine
): Promise<string[]> {
  if (credentialTypeName !== AI_VENDOR_KEY_TYPE) {
    return [];
  }
  const driverClass = (values['driverClass'] ?? '').trim();
  if (!driverClass) {
    throw new Error('An AI vendor key needs a driver class.');
  }
  const vendorsEntity = new Metadata().Entities.find(e => e.Name === AI_VENDORS_ENTITY);
  const result = await new RunView().RunView<{ VendorID: string }>({
    EntityName: 'MJ: AI Model Vendors',
    ExtraFilter: `DriverClass = '${driverClass.replace(/'/g, "''")}'`,
    Fields: ['VendorID'],
    MaxRows: 1,
    ResultType: 'simple',
  });
  const vendorID = result.Success ? result.Results[0]?.VendorID : undefined;
  if (!vendorsEntity || !vendorID) {
    throw new Error(`No AI vendor uses the driver class "${driverClass}".`);
  }
  const use = engine.CredentialUses.find(
    u => UUIDsEqual(u.ReferenceEntityID, vendorsEntity.ID) && UUIDsEqual(u.ReferenceRecordID, vendorID) && u.Status === 'Active'
  );
  if (!use) {
    throw new Error(`Credentials for this AI vendor are not enabled (its BCSaaS Credential Use is inactive).`);
  }
  return [use.ID];
}
